/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Image,
  InsertEmoticon,
  KeyboardArrowDown,
  OpenInFull,
  TextSnippet,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Divider,
  Icon,
  IconButton,
  IconProps,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { FileItem } from 'api/files';
import EditableTextField from 'components/editableTextField';
import FileExplorer from 'components/FileExplorer';
import { useCurrentModal, useModal } from 'components/modal';
import { useFileUpload } from 'hooks/useFileUpload';
import React, { useCallback, useMemo, useState } from 'react';
import { FinsightEntityField } from 'shared';
import useSWR from 'swr';
import { formatName } from 'utils/fields';

import { ViewType } from '.';

export function ImageRenderer({
  field,
  value,
  editable,
  onChange,
  viewType,
}: {
  field: FinsightEntityField;
  value: unknown;
  editable?: boolean;
  onChange?: (value: unknown) => Promise<void>;
  viewType: ViewType;
}) {
  const { showModal } = useModal();
  const fileOnly = field.schema.type === 'file';

  let icon = null;
  let emoji = null;

  if (!fileOnly) {
    if (typeof value === 'string' && value.startsWith('icon:')) {
      const iconURI = new URL(value);
      icon = {
        name: iconURI.pathname.toLowerCase(),
        color: iconURI.searchParams.get('color') || undefined,
      };
    }

    if (typeof value === 'string' && value.startsWith('emoji:')) {
      emoji = value.replace('emoji:', '');
    }
  }

  const fileInfo = useMemo(() => {
    if (!fileOnly) return null;
    try {
      const parsedUrl = new URL(value as string);
      const lastSegment = parsedUrl.pathname.split('/').pop();
      const realFileName = parsedUrl.searchParams.get('bf.name');
      const dots = (realFileName ?? lastSegment)?.split('.');

      let truncated = false;
      let firstPart = dots?.slice(0, -1).join('.');
      if (firstPart && firstPart.length > 15) {
        firstPart = firstPart.slice(0, 15);
        truncated = true;
      }
      const lastPart = dots?.[dots.length - 1].slice(0, 5);

      return {
        pathname: `${firstPart}${truncated ? '...' : '.'}${lastPart}`,
        host: parsedUrl.host,
        image:
          lastPart && ['png', 'jpg', 'jpeg', 'webp'].includes(lastPart)
            ? (value as string)
            : null,
      };
    } catch {
      return null;
    }
  }, [fileOnly, value]);

  if (editable) {
    return (
      <Stack
        sx={{
          borderStyle: 'solid',
          borderWidth: 1,
          borderColor: 'divider',
          borderRadius: 2,
          backgroundColor: 'rgb(10,10,10)',
          overflow: 'hidden',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: '1fr',
          '& > *': {
            gridRow: 1,
            gridColumn: 1,
          },
          '& img': {
            transition: 'filter 0.3s ease',
          },
          '& .img-edit-button, & .img-max-button': {
            transition: 'opacity 0.3s ease',
          },
          '&:hover': {
            '.img-edit-button': {
              opacity: 1,
            },
            '.img-max-button': {
              opacity: 0.8,
            },
            img: {
              filter: 'brightness(0.7)',
            },
          },
        }}
      >
        {typeof value === 'string' && value ? (
          fileOnly ? (
            <Stack alignItems="center" justifyContent="center" py={4} gap={1}>
              <IconButton
                sx={{
                  maxWidth: '80%',
                }}
                onClick={() => {
                  window.open(value as string, '_blank');
                }}
              >
                {fileInfo?.image ? (
                  <img
                    src={`https://web.blackframe.app/cdn-cgi/image/w=250,quality=50,onerror=redirect/${value}`}
                    style={{
                      width: '100%',
                      height: 'auto',
                      aspectRatio: '1/1',
                      objectFit: 'cover',
                      borderRadius: 4,
                      maxHeight: 400,
                      maxWidth: 400,
                      minWidth: 0,
                      minHeight: 0,
                    }}
                  />
                ) : (
                  <TextSnippet
                    sx={{
                      color: '#ff2255',
                      fontSize: '6em',
                    }}
                  />
                )}
              </IconButton>
              <Stack alignItems="center" justifyContent="center">
                <Typography variant="h6">{fileInfo?.pathname}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {fileInfo?.host}
                </Typography>
              </Stack>
            </Stack>
          ) : icon ? (
            <RenderIcon
              icon={icon}
              sx={{
                m: 2,
                fontSize: 200,
                justifySelf: 'center',
              }}
            />
          ) : emoji ? (
            <Box
              sx={{
                m: 2,
                fontSize: 200,
                justifySelf: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {emoji}
            </Box>
          ) : (
            <img
              src={`https://web.blackframe.app/cdn-cgi/image/w=250,quality=50,onerror=redirect/${value}`}
              style={{
                // width: 'fit-content',
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                borderRadius: 4,
                maxHeight: 400,
                maxWidth: 400,
                minWidth: 0,
                minHeight: 0,
              }}
            />
          )
        ) : (
          <Typography textAlign="center" alignSelf="center">
            {fileOnly ? 'No File' : 'No Image'}
          </Typography>
        )}
        <Button
          onClick={() => {
            showModal(
              fileOnly ? 'Edit File' : 'Edit Image URL',
              <ImageEditorModal
                field={field}
                value={value}
                onChange={onChange}
                fileOnly={fileOnly}
              />,
            );
          }}
          className="img-edit-button"
          sx={{
            alignSelf: 'end',
            minHeight: 50,
            borderTopStyle: 'solid',
            borderTopWidth: 1,
            borderTopColor: 'divider',
            opacity: 0,
            background: 'transparent',
            backdropFilter: 'blur(20px) saturate(2)',
            color: 'text.primary',
            '&:hover': {
              backdropFilter: 'blur(20px) saturate(2)',
            },
          }}
        >
          Edit
        </Button>
        <IconButton
          size="small"
          className="img-max-button"
          onClick={() => {
            showModal(
              fileOnly ? 'File' : 'Image',
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="100%"
              >
                {icon ? (
                  <RenderIcon
                    icon={icon}
                    sx={{
                      fontSize: 400,
                    }}
                  />
                ) : emoji ? (
                  <Box
                    sx={{
                      fontSize: 400,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {emoji}
                  </Box>
                ) : (
                  <img
                    src={value as string}
                    alt={formatName(field.name ?? '')}
                    style={{
                      maxHeight: '90vh',
                      maxWidth: '90vw',
                      objectFit: 'contain',
                    }}
                  />
                )}
              </Box>,
            );
          }}
          sx={{
            opacity: 0,
            display: fileOnly ? 'none' : 'flex',
            alignSelf: 'start',
            justifySelf: 'start',
            color: 'text.primary',
            background: 'transparent',
            backdropFilter: 'blur(20px) saturate(2)',
            m: 1,
            '&:hover': {
              backdropFilter: 'blur(20px) saturate(2)',
            },
          }}
        >
          <OpenInFull />
        </IconButton>
      </Stack>
    );
  }

  if (viewType === 'card' && field.name === 'image') {
    return null;
  }

  if (typeof value !== 'string' || !value) return null;

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      {fileOnly ? (
        <TextSnippet />
      ) : icon ? (
        <RenderIcon icon={icon} />
      ) : emoji ? (
        <Box
          sx={{
            fontSize: viewType === 'list' ? 24 : 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {emoji}
        </Box>
      ) : (
        <img
          src={`https://web.blackframe.app/cdn-cgi/image/w=250,quality=50,onerror=redirect/${value}`}
          alt={formatName(field.name ?? '')}
          style={{
            objectFit: 'contain',
            borderRadius: '4px',
            ...(viewType === 'list'
              ? {
                  maxWidth: '96px',
                  maxHeight: '48px',
                }
              : {}),
            ...(viewType === 'card_bg'
              ? {
                  objectFit: 'cover',
                }
              : {}),
          }}
          onError={(e) => {
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
      )}
      <Typography
        variant="caption"
        color="error"
        className="hidden"
        style={{ display: 'none' }}
      >
        {fileOnly ? 'Failed to load file' : 'Failed to load image'}
      </Typography>
    </Box>
  );
}

type IconType = {
  name: string;
  color?: string;
};

function RenderIcon(props: IconProps & { icon: IconType }) {
  const { icon, ...rest } = props;
  return (
    <Icon
      {...rest}
      sx={{
        ...rest.sx,
        color: icon.color,
      }}
    >
      {icon.name}
    </Icon>
  );
}

function ImageEditorModal(props: {
  field: FinsightEntityField;
  value: unknown;
  onChange?: (value: unknown) => Promise<void>;
  fileOnly?: boolean;
}) {
  const { field, value, onChange, fileOnly = false } = props;
  const modalCtx = useCurrentModal();
  const { uploadFile, uploadProgress } = useFileUpload();

  const [tab, setTab] = useState(() => {
    if (typeof value === 'string' && value.startsWith('icon:')) return 1;
    if (typeof value === 'string' && value.startsWith('emoji:')) return 2;
    return 0;
  });

  const [showIconPicker, setShowIconPicker] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [icon, setIcon] = useState<IconType | null>(() => {
    if (typeof value === 'string' && value.startsWith('icon:')) {
      const iconURI = new URL(value);
      return {
        name: iconURI.pathname.toLowerCase(),
        color: iconURI.searchParams.get('color') || undefined,
      };
    }
    return null;
  });

  const [emoji, setEmoji] = useState<string>(() => {
    if (typeof value === 'string' && value.startsWith('emoji:')) {
      return value.replace('emoji:', '');
    }
    return '';
  });

  const handleFileUpload = useCallback(
    async (file: File) => {
      if (!file) return;
      if (!fileOnly && !file.type.startsWith('image/')) return;

      setIsUploading(true);
      try {
        const { workspaceId, uploadId, fileName } = await uploadFile(file);

        // Extract file extension from fileName
        const extensionMatch = fileName.match(/\.([^.]+)$/);
        const extension = extensionMatch ? extensionMatch[1] : 'png';

        // Construct the URL in the requested format
        const imageUrl = `https://s3.microart.app/finsight-uploads/${workspaceId}/${uploadId}.${extension}`;

        // Close modal and update the value
        modalCtx?.closeModal();
        return onChange?.(imageUrl);
      } catch (error) {
        console.error('Failed to upload file:', error);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadFile, onChange, modalCtx, fileOnly],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileUpload(files[0]);
      }
    },
    [handleFileUpload],
  );

  const handlePaste = useCallback(
    async (e: React.ClipboardEvent) => {
      const items = e.clipboardData.items;

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (fileOnly && item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            handleFileUpload(file);
            break;
          }
        } else if (!fileOnly && item.type.indexOf('image') !== -1) {
          const file = item.getAsFile();
          if (file) {
            handleFileUpload(file);
            break;
          }
        }
      }
    },
    [handleFileUpload, fileOnly],
  );

  const urlStack = (
    <Stack spacing={2}>
      <FileExplorer
        onChange={(v) => {
          const url =
            typeof v === 'object' && v !== null && 'url' in v
              ? (v as FileItem).url
              : (v as string);
          modalCtx?.closeModal();
          return onChange?.(url);
        }}
      />

      <Typography variant="body2" color="text.secondary" align="center">
        {fileOnly
          ? 'Or enter file URL manually:'
          : 'Or enter image URL manually:'}
      </Typography>

      <EditableTextField
        notBlank={field.schema.required}
        variant="outlined"
        label={formatName(field.name ?? '')}
        value={value}
        onChange={async (v) => {
          modalCtx?.closeModal();
          return onChange?.(v);
        }}
        placeholder={fileOnly ? 'Enter file URL' : 'Enter image URL'}
        InputProps={{
          autoComplete: 'off',
        }}
      />
    </Stack>
  );

  return (
    <Stack pt={1} gap={4}>
      {!fileOnly && (
        <ToggleButtonGroup fullWidth size="small">
          <ToggleButton
            selected={tab === 0}
            onClick={() => setTab(0)}
            value={0}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <Image fontSize="small" />
              <Typography variant="button" mb={-0.3}>
                Image
              </Typography>
            </Stack>
          </ToggleButton>
          <ToggleButton
            value={1}
            selected={tab === 1}
            onClick={() => setTab(1)}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <InsertEmoticon fontSize="small" />
              <Typography variant="button" mb={-0.3}>
                Icon
              </Typography>
            </Stack>
          </ToggleButton>
          <ToggleButton
            value={2}
            selected={tab === 2}
            onClick={() => setTab(2)}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <Typography variant="button" mb={-0.3}>
                😀
              </Typography>
              <Typography variant="button" mb={-0.3}>
                Emoji
              </Typography>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {fileOnly ? (
        urlStack
      ) : tab === 0 ? (
        urlStack
      ) : tab === 1 ? (
        <Stack>
          {icon && (
            <RenderIcon
              icon={icon}
              sx={{
                fontSize: 256,
                alignSelf: 'center',
              }}
            />
          )}
          <Button
            onClick={() => setShowIconPicker(!showIconPicker)}
            endIcon={
              <KeyboardArrowDown
                style={{
                  transition: 'transform 0.3s ease',
                  transform: showIconPicker
                    ? 'rotate(-180deg)'
                    : 'rotate(0deg)',
                }}
              />
            }
          >
            {showIconPicker ? 'Hide' : 'Show'} Icon Picker
          </Button>
          {showIconPicker && (
            <AllIconsGrid
              onChange={(icon) => {
                setShowIconPicker(false);
                setIcon((prev) => ({
                  ...prev,
                  name: icon,
                }));
              }}
            />
          )}
          <Divider />
          <Stack direction="row" alignItems="center" gap={2} my={2}>
            <Stack
              sx={{
                borderRadius: 20,
                overflow: 'hidden',
                width: 'fit-content',
              }}
            >
              <input
                type="color"
                value={icon?.color || '#000000'}
                onChange={(e) =>
                  setIcon((prev) => ({
                    ...prev,
                    color: e.target.value,
                  }))
                }
                style={{
                  border: 'none',
                  background: 'none',
                  width: 40,
                  height: 40,
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  transform: 'scale(2)',
                }}
              />
            </Stack>
            <Stack>
              <Typography>Pick Icon Color</Typography>
              <Typography variant="caption" color="text.secondary">
                Click the color circle on the left to pick a color
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Stack direction="row" gap={1} mt={2}>
            <Button
              fullWidth
              color="error"
              onClick={() => modalCtx?.closeModal()}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                const iconURL = `icon:${icon?.name}${icon?.color ? `?color=${encodeURIComponent(icon?.color)}` : ''}`;
                modalCtx?.closeModal();
                return onChange?.(iconURL);
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack>
          {emoji && (
            <Box
              sx={{
                fontSize: 256,
                alignSelf: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {emoji}
            </Box>
          )}
          <TextField
            label="Emoji"
            variant="outlined"
            value={emoji}
            onChange={(e) => setEmoji(e.target.value)}
            placeholder="Enter or paste an emoji"
            InputProps={{
              autoComplete: 'off',
            }}
            helperText="Enter or paste an emoji from your OS emoji picker"
          />
          <Stack direction="row" gap={1} mt={2}>
            <Button
              fullWidth
              color="error"
              onClick={() => modalCtx?.closeModal()}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                const emojiURL = `emoji:${emoji}`;
                modalCtx?.closeModal();
                return onChange?.(emojiURL);
              }}
            >
              Save
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
}

function AllIconsGrid(props: { onChange: (icon: string) => void }) {
  const icons = useSWR(
    '/material_icons_names.csv',
    async (url) => {
      const response = await fetch(url);
      const data = await response.text();
      return data.split('\n');
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  );

  const [search, setSearch] = useState('');

  const filteredIcons = useMemo(() => {
    return icons.data?.filter((icon) =>
      icon.toLowerCase().includes(search.toLowerCase()),
    );
  }, [icons.data, search]);

  return (
    <Stack
      gap={2}
      sx={{
        borderStyle: 'solid',
        borderWidth: 1,
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <TextField
        label="Search Icons"
        variant="outlined"
        size="small"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{
          mx: 1.5,
          mt: 1.5,
        }}
      />
      <Divider />
      <Stack
        flexWrap="wrap"
        direction="row"
        gap={1}
        maxHeight={400}
        maxWidth={400}
        overflow="auto"
      >
        {filteredIcons?.map((icon) => (
          <IconButton
            key={icon}
            onClick={() => props.onChange(icon)}
            sx={{
              '& .material-icons': {
                fontSize: '40px !important',
              },
            }}
          >
            <Icon>{icon}</Icon>
          </IconButton>
        ))}
      </Stack>
    </Stack>
  );
}

export function isValidImage(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  field: FinsightEntityField,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  value: unknown,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  viewType: 'list',
) {
  if (typeof value !== 'string' || !value) return false;
  return true;
}
